// Merkan · Aufruf der KI-Funktion (Edge Function "ki")
import { supabase } from './supabase';
import { t } from './sprache';

export interface KiAntwort<T = unknown> {
  ok: boolean;
  hinweis?: string;
  nicht_verbunden?: boolean;
  kontingent?: boolean;
  ergebnis?: T;
  vorschlag_id?: string;
  antwort?: string;
  uebersetzung?: string;
  [k: string]: unknown;
}

/** Ruft eine KI-Aktion auf. Fehler kommen immer als { ok: false, hinweis } zurück, nie als Ausnahme. */
export async function ki<T = unknown>(mandantId: string, aktion: string, daten: Record<string, unknown> = {}, versuch = 1): Promise<KiAntwort<T>> {
  try {
    const { data, error } = await supabase.functions.invoke('ki', { body: { aktion, mandant_id: mandantId, ...daten } });
    if (error) {
      // Fehlerantworten der Function tragen trotzdem einen JSON-Körper mit hinweis
      const ctx = (error as { context?: Response }).context;
      // Kaltstart oder Gateway-Fehler der Function: einmal nach kurzer Pause wiederholen
      if (versuch < 2 && ctx && [502, 503, 504].includes(ctx.status)) {
        await new Promise((r) => setTimeout(r, 1500));
        return ki<T>(mandantId, aktion, daten, versuch + 1);
      }
      if (ctx && typeof ctx.json === 'function') {
        try {
          const j = await ctx.json();
          return { ok: false, hinweis: j.hinweis ?? t('{ki} konnte die Anfrage nicht bearbeiten.'), ...j };
        } catch { /* weiter unten */ }
      }
      return { ok: false, hinweis: t('{ki} ist gerade nicht erreichbar. Bitte später erneut versuchen.') };
    }
    return data as KiAntwort<T>;
  } catch {
    return { ok: false, hinweis: t('{ki} ist gerade nicht erreichbar. Bitte später erneut versuchen.') };
  }
}

/** Rückmeldung zu einem KI-Vorschlag: Grundlage für das Lernen */
export async function kiRueckmeldung(vorschlagId: string, status: 'angenommen' | 'korrigiert' | 'abgelehnt', korrektur?: Record<string, unknown>) {
  await supabase.rpc('ki_rueckmeldung', { p_vorschlag: vorschlagId, p_status: status, p_korrektur: korrektur ?? null });
}

/** Entscheidung merken (z. B. gewählter Lieferant je Marke), Grundlage für Vorschläge */
export async function entscheidungMerken(mandantId: string, art: string, schluessel: string, wert: string) {
  await supabase.rpc('entscheidung_merken', { p_mandant: mandantId, p_art: art, p_schluessel: schluessel, p_wert: wert });
}

export async function vorschlaege(mandantId: string, art: string, schluessel: string): Promise<{ wert: string; anteil: number; anzahl: number }[]> {
  const { data } = await supabase.rpc('vorschlag_aus_entscheidungen', { p_mandant: mandantId, p_art: art, p_schluessel: schluessel, p_anzahl: 3 });
  return (data as { wert: string; anteil: number; anzahl: number }[] | null) ?? [];
}
